import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AdminMission } from '../../../models/mission-admin';
import { MissionAdminService } from '../../../services/mission-admin.service';
import { AdminPageHeaderComponent } from '../../shared/admin-page-header/admin-page-header.component';

interface MissionQueryMetricReference {
  field: string;
  game: string;
  description: string;
  example: string;
}

@Component({
  selector: 'app-mission-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, AdminPageHeaderComponent],
  templateUrl: './mission-detail.component.html',
  styleUrl: './mission-detail.component.scss',
})
export class MissionDetailComponent implements OnInit {
  missionId?: number;
  isNew = true;

  readonly queryMetricReferences: MissionQueryMetricReference[] = [
    {
      field: 'player_won',
      game: 'League of Legends',
      description: 'Vitória ou derrota do player na partida da Riot.',
      example: 'player_won==true',
    },
    {
      field: 'player_deaths',
      game: 'League of Legends',
      description: 'Quantidade de mortes do player na partida.',
      example: 'player_deaths<=5',
    },
    {
      field: 'player_kda',
      game: 'League of Legends',
      description: 'KDA final do player retornado pela Riot.',
      example: 'player_kda>=4.5',
    },
    {
      field: 'player_double_kills',
      game: 'League of Legends',
      description: 'Quantidade de Double Kills do player.',
      example: 'player_double_kills>=1',
    },
    {
      field: 'player_triple_kills',
      game: 'League of Legends',
      description: 'Quantidade de Triple Kills do player.',
      example: 'player_triple_kills>=1',
    },
    {
      field: 'player_quadra_kills',
      game: 'League of Legends',
      description: 'Quantidade de Quadra Kills do player.',
      example: 'player_quadra_kills>=1',
    },
    {
      field: 'player_penta_kills',
      game: 'League of Legends',
      description: 'Quantidade de Penta Kills do player.',
      example: 'player_penta_kills>=1',
    },
    {
      field: 'won',
      game: 'League of Legends',
      description: 'Pontuação do componente de vitória no score GB.',
      example: 'won>0',
    },
    {
      field: 'kda',
      game: 'League of Legends',
      description: 'Pontuação do componente de KDA no score GB.',
      example: 'kda>=50',
    },
    {
      field: 'turrets',
      game: 'League of Legends',
      description: 'Pontuação de torres destruídas no score GB.',
      example: 'turrets>=20',
    },
    {
      field: 'gold_score',
      game: 'League of Legends',
      description: 'Pontuação de ouro no score GB.',
      example: 'gold_score>=50',
    },
    {
      field: 'vision_score',
      game: 'League of Legends',
      description: 'Pontuação de visão no score GB.',
      example: 'vision_score>=15',
    },
    {
      field: 'baron',
      game: 'League of Legends',
      description: 'Pontuação de Baron no score GB.',
      example: 'baron>=1',
    },
    {
      field: 'atakhan',
      game: 'League of Legends',
      description: 'Pontuação de Atakhan no score GB.',
      example: 'atakhan>=1',
    },
    {
      field: 'elder_dragon',
      game: 'League of Legends',
      description: 'Pontuação de Dragão Ancião no score GB.',
      example: 'elder_dragon>=1',
    },
    {
      field: 'elemental_dragon',
      game: 'League of Legends',
      description: 'Pontuação de Dragão Elemental no score GB.',
      example: 'elemental_dragon>=1',
    },
    {
      field: 'herald',
      game: 'League of Legends',
      description: 'Pontuação de Arauto no score GB.',
      example: 'herald>=1',
    },
    {
      field: 'horde',
      game: 'League of Legends',
      description: 'Pontuação de Vastilarvas no score GB.',
      example: 'horde>=1',
    },
    {
      field: 'death_zero_bonus',
      game: 'League of Legends',
      description: 'Bônus do score GB quando o player termina com zero mortes.',
      example: 'death_zero_bonus>0',
    },
    {
      field: 'score',
      game: 'League of Legends / Counter-Strike',
      description: 'Score final calculado pela Gamers Brawl.',
      example: 'score>=350',
    },
    {
      field: 'active_user',
      game: 'Counter-Strike',
      description: 'Identificador do player analisado no resumo do CS.',
      example: 'active_user==76561198000000000',
    },
    {
      field: 'game_duration',
      game: 'Counter-Strike',
      description: 'Duração da partida analisada em segundos.',
      example: 'game_duration>=900',
    },
    {
      field: 'kills',
      game: 'Counter-Strike',
      description: 'Quantidade de kills no resumo analisado do CS.',
      example: 'kills>=20',
    },
    {
      field: 'assists',
      game: 'Counter-Strike',
      description: 'Quantidade de assistências no resumo analisado do CS.',
      example: 'assists>=5',
    },
    {
      field: 'match_victory',
      game: 'Counter-Strike',
      description: 'Indica vitória da partida no resumo analisado do CS.',
      example: 'match_victory>=1',
    },
    {
      field: 'match_draw',
      game: 'Counter-Strike',
      description: 'Indica empate da partida no resumo analisado do CS.',
      example: 'match_draw==0',
    },
    {
      field: 'victories',
      game: 'Counter-Strike',
      description: 'Quantidade de rounds vencidos no resumo analisado do CS.',
      example: 'victories>=13',
    },
    {
      field: 'bomb_planted',
      game: 'Counter-Strike',
      description: 'Quantidade de bombas plantadas no resumo analisado do CS.',
      example: 'bomb_planted>=1',
    },
    {
      field: 'bomb_defused',
      game: 'Counter-Strike',
      description: 'Quantidade de bombas defusadas no resumo analisado do CS.',
      example: 'bomb_defused>=1',
    },
  ];
  
  isLoading = false;
  feedbackError = '';
  feedbackSuccess = '';

  form: Partial<AdminMission> = {
    name: '',
    description: '',
    game: 'LEAGUE_OF_LEGENDS',
    metric: '',
    query: '',
    tier: 1,
    multiplier: 1.0,
    is_active: true,
  };

  constructor(
    private route: ActivatedRoute, 
    private router: Router,
    private missionService: MissionAdminService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'nova') {
      this.missionId = Number(idParam);
      this.isNew = false;
      this.loadMission();
    }
  }

  loadMission(): void {
    if (!this.missionId) return;
    this.isLoading = true;
    this.missionService.detail(this.missionId).subscribe({
      next: (data) => {
        this.form = { ...data };
        this.isLoading = false;
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Erro ao carregar a missão.');
        this.isLoading = false;
      }
    });
  }

  saveMission(): void {
    this.feedbackError = '';
    this.feedbackSuccess = '';

    if (!this.form.name || !this.form.description || !this.form.game || !this.form.metric || this.form.tier === undefined || this.form.multiplier === undefined) {
      this.feedbackError = 'Preencha todos os campos obrigatórios (Nome, Descrição, Jogo, Métrica, Tier e Multiplicador).';
      return;
    }

    const payload = { ...this.form };

    const request$ = this.isNew
      ? this.missionService.create(payload)
      : this.missionService.update(this.missionId!, payload);

    request$.subscribe({
      next: (savedMission) => {
        this.feedbackSuccess = 'Template de missão salvo com sucesso!';
        if (this.isNew) {
          this.router.navigate(['/dashboard/missions', savedMission.id]);
        } else {
          this.form = { ...savedMission };
        }
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Erro ao salvar o template.');
      },
    });
  }

  deactivateMission(): void {
    if (!this.missionId) return;
    
    if (confirm('Tem certeza que deseja inativar esta missão? Ela não aparecerá em novas ofertas.')) {
      this.missionService.deactivate(this.missionId).subscribe({
        next: () => {
          this.feedbackSuccess = 'Template desativado com sucesso.';
          this.loadMission();
        },
        error: (error) => {
          this.feedbackError = this.extractErrorMessage(error, 'Erro ao desativar a missão.');
        }
      });
    }
  }

  private extractErrorMessage(error: any, fallback: string): string {
    const response = error?.error;
    if (!response) return fallback;
    if (typeof response === 'string') return response;
    if (typeof response?.detail === 'string') return response.detail;
    return fallback;
  }
}
